import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Calendar, UserPlus, Heart } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import apiClient from "../api/apiClient";
import "./NotificationPopup.css";
import { useNotifications } from "../context/NotificationContext";

const NotificationPopup = ({ onClose }) => {
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.user);

  // Extract newNotification from context
  const { setUnreadCount, newNotification } = useNotifications();

  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await apiClient.get(`/notifications/${profile?.id}`);
        if (response.data.status)
          setNotifications(response.data.data.slice(0, 5));
      } catch (error) {
        console.error(error);
      }
    };
    if (profile?.id) fetchNotifications();
  }, [profile?.id]);

  useEffect(() => {
    if (newNotification) {
      setNotifications((prev) => {
        // Prevent duplicates
        if (prev.some((n) => n.id === newNotification.id)) return prev;
        // Prepend new notification and keep only the latest 5
        return [newNotification, ...prev].slice(0, 5);
      });
    }
  }, [newNotification]);

  const getIcon = (title) => {
    const t = (title || "").toLowerCase();
    if (t.includes("treat"))
      return <Heart size={16} className="pop-icon-heart" />;
    if (t.includes("event"))
      return <Calendar size={16} className="pop-icon-blue" />;
    if (t.includes("request"))
      return <UserPlus size={16} className="pop-icon-blue" />;
    return <MessageSquare size={16} className="pop-icon-blue" />;
  };

  const markAsRead = async (id) => {
    try {
      const response = await apiClient.put(
        `/notifications/update-notification-ById/${id}`,
        { is_read: true },
      );
      if (response.data.status) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error updating notification:", error);
    }
  };

  return (
    <div className="notif-popup-window" onClick={(e) => e.stopPropagation()}>
      <div className="notif-popup-tabs">
        {[""].map((tab) => (
          <button
            key={tab}
            className={activeTab === tab ? "active" : ""}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="notif-popup-list">
        {notifications.map((notif) => (
          <div
            key={notif.id}
            className="notif-popup-item"
            onClick={() => {
              if (!notif.is_read) markAsRead(notif.id);

              if ((notif.title || "").toLowerCase().includes("treat")) {
                navigate("/receive-a-treat", {
                  state: { notificationId: notif.id },
                });
              } else if ((notif.title || "").toLowerCase().includes("walk")) {
                navigate("/receive-a-book", {
                  state: {
                    notificationId: notif.id,
                  },
                });
              }
            }}
          >
            <div
              className={`notif-popup-icon-circle ${(notif.title || "").toLowerCase().includes("treat") ? "blue-bg-popup" : "blue-bg-popup"}`}
            >
              {getIcon(notif.title)}
            </div>
            <div className="notif-popup-content">
              <div className="notif-popup-row">
                <span className="notif-popup-title">{notif.title}</span>
                {!notif.is_read && <div className="unread-indicator" />}
              </div>
              <p className="notif-popup-msg">{notif.message}</p>
              <div className="notif-popup-footer">
                <span className="notif-popup-time">
                  {formatDistanceToNow(new Date(notif.created_at))} ago
                </span>
                <button className="notif-popup-reply">View</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="notif-popup-bottom">
        <span
          onClick={() => {
            onClose();
            navigate("/notifications");
          }}
        >
          View all notifications
        </span>
      </div>
    </div>
  );
};

export default NotificationPopup;
